import { BCButton, BCNotification, findMessage, NotificationComponent } from '@bimser/components';
import { createGuid, createSyncAction, IBaseAction, lazyRetry, RxEventEmitter } from '@bimser/core';
import { HubConnection } from '@microsoft/signalr';
import _get from 'lodash/get';
import { call, put, takeEvery } from 'redux-saga/effects';
import * as ActionTypes from '../../action/actionTypes';
import { setDeviceTabIdAction } from '../../components/HubConnectionState/actions';
import HTTPRequest from '../../services/HTTPRequest';
type GetCreateType = () => HubConnection;

const SendMessageMethod = 'ReceiveMessages'; //RealtimeInteractionWebAPI\WebInterfaceWebAPIParameters => SendMessageMethod { get { return "ReceiveMessages"; } }
enum HubConnectionState {
	Disconnected = 'Disconnected',
	Connecting = 'Connecting',
	Connected = 'Connected',
	Disconnecting = 'Disconnecting',
	Reconnecting = 'Reconnecting'
}
const url: string = _get(
	window as any,
	'serviceConfig.urls.socket',
	window.origin + '/realtimeinteractionhub'
);

const SignalRConnectMessageId = 'SignalRConnectMessage';

export default function* watchGetAllHubConnection() {
	if (url) {
		yield takeEvery(ActionTypes.GENERAL_INITIALIZER_TRANSACTION_ACTION, handleGetHubConnection);
	}
}

function* handleGetHubConnection() {
	try {
		const token =
			HTTPRequest.defaultHeaders.Authorization.indexOf('Bearer') != -1
				? HTTPRequest.defaultHeaders.Authorization.substring(7)
				: HTTPRequest.defaultHeaders.Authorization;
		const encryptedData = HTTPRequest.defaultHeaders['bimser-encrypted-data'];
		const language = HTTPRequest.defaultHeaders['bimser-language'];
		const deviceTabId = createGuid();

		const connection = yield hubConnection();
		const socket = connection.get();

		yield put(setDeviceTabIdAction(deviceTabId));

		window.addEventListener(
			'beforeunload',
			function (event) {
				LogoutMessage(socket, token, encryptedData, language);
			},
			false
		);

		while (true) {
			if (socket.state == HubConnectionState.Disconnected) {
				yield socket.start();

				socket.onreconnecting(error => {
					console.error(error.message);
					showToaster(() => {
						BCNotification.warning({
							toastId: SignalRConnectMessageId,
							message: findMessage.get('103657'),
							description: findMessage.get('103622'),
							autoClose: false,
							closeButton: false,
							btn: <HideToasterButton />
						});
					});
				});

				socket.onreconnected(() => {
					LoginMessage(socket, token, encryptedData, language, deviceTabId);
					showToaster(() => {
						BCNotification.update(SignalRConnectMessageId, {
							render: () => (
								<NotificationComponent
									message={findMessage.get('103657')}
									description={findMessage.get('103620')}
								/>
							),
							autoClose: 10000,
							type: 'success',
							closeButton: true
						});
					});
				});

				socket.onclose(() => {
					showToaster(() => {
						BCNotification.update(SignalRConnectMessageId, {
							render: () => (
								<NotificationComponent
									message={findMessage.get('103657')}
									description={findMessage.get('103621')}
									btn={
										<>
											<ReconnectButton socket={socket} />
											<HideToasterButton />
										</>
									}
								/>
							),
							autoClose: false,
							type: 'error',
							closeButton: true
						});
					});
				});

				if (socket.state == 'Connected') {
					LoginMessage(socket, token, encryptedData, language, deviceTabId);
				}
			}
			const response: IBaseAction<any> = yield call(connect, socket);
			RxEventEmitter.emit('SYSTEM_SOCKET', response);
			yield put(createSyncAction(response.type, response.payload));
		}
	} catch (e) {
		writeLog(e);
	}
}

const showToaster = (callback: () => void) => {
	const isHiddenValue = localStorage.getItem('hideSocketReconnectionToaster');
	if (isHiddenValue !== '1') callback();
};

const ReconnectButton: React.FC<{ socket }> = props => {
	return (
		<BCButton
			text="Reconnect"
			size="small"
			onClick={() => {
				props.socket
					.start()
					.then(() => {
						showToaster(() => {
							BCNotification.update(SignalRConnectMessageId, {
								render: () => (
									<NotificationComponent
										message={findMessage.get('103657')}
										description={findMessage.get('103620')}
									/>
								),
								autoClose: 10000,
								type: 'success',
								closeButton: true
							});
						});
					})
					.catch(e => {
						console.error(e.message);
						showToaster(() => {
							BCNotification.update(SignalRConnectMessageId, {
								render: () => (
									<NotificationComponent
										message={findMessage.get('103657')}
										description={findMessage.get('103619')}
										btn={
											<>
												<ReconnectButton socket={props.socket} />
												<HideToasterButton />
											</>
										}
									/>
								),
								autoClose: false,
								type: 'warning',
								closeButton: false
							});
						});
					});
			}}
		/>
	);
};

const HideToasterButton: React.FC = () => {
	return (
		<BCButton
			text={findMessage.get('103618')}
			onClick={() => {
				BCNotification.destroy(SignalRConnectMessageId);
				localStorage.setItem('hideSocketReconnectionToaster', '1');
			}}
			size="small"
			type="link"
			style={{ marginLeft: '5px' }}
		/>
	);
};

function connect(socket: any) {
	return new Promise(resolve => {
		socket.on(SendMessageMethod, (action: string) => {
			const _action: IBaseAction<any> = JSON.parse(action);
			resolve(_action);
		});
	});
}

const hubConnection = () =>
	lazyRetry(() =>
		import(/* webpackChunkName: "signalr" */ '@microsoft/signalr').then(
			({ HubConnectionBuilder, LogLevel }) => {
				let state: HubConnection,
					pub: { get: GetCreateType; create: GetCreateType } = {
						get: null,
						create: null
					};
				pub.get = () => {
					if (!state) {
						state = pub.create();
					}
					return state;
				};

				pub.create = () =>
					new HubConnectionBuilder()
						.withUrl(url)
						.configureLogging(LogLevel.Information)
						.withAutomaticReconnect()
						.build();

				return pub;
			}
		)
	);

function LoginMessage(
	socket: HubConnection,
	token: string,
	encryptedData: string,
	language: string,
	deviceTabId: string
) {
	socket.invoke('LoginMessageFromClientV2', token, encryptedData, language, deviceTabId);
}

function LogoutMessage(
	socket: HubConnection,
	token: string,
	encryptedData: string,
	language: string
) {
	socket.invoke('LogoutMessageFromClient', token, encryptedData, language);
}

function writeLog(e: any) {
	console.log(`HubConnection Error : ${e}`);
}
